import { Typography } from "@/components/atoms";
import { ChevronDownIcon, ChevronUpIcon } from "@/components/atoms/icons";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React, { useState } from "react";
import { Pressable, Platform, StyleSheet, View } from "react-native";
import { FaqItem } from "../services/faqService";

interface FaqListProps {
  readonly items: FaqItem[];
}

function FaqListItem({
  item,
  index,
  isExpanded,
  onToggle,
  questionLabel,
  answerLabel,
  colors,
}: {
  item: FaqItem;
  index: number;
  isExpanded: boolean;
  onToggle: (idx: number) => void;
  questionLabel: string;
  answerLabel: string;
  colors: any;
}) {
  const { isRTL } = useI18nContext();

  const webDirection = isRTL ? "row" : "row-reverse";
  const mobileDirection = isRTL ? "row-reverse" : "row";
  const headerDirection = Platform.OS === 'web' ? webDirection : mobileDirection;
  const contentDirection = Platform.OS === 'web' && isRTL ? "row-reverse" : "row";

  return (
    <View
      style={[styles.faqItem, { borderColor: colors.outlineInactive }]}
    >
      <Pressable
        onPress={() => onToggle(index)}
        style={[
          styles.faqHeader,
          {
            alignItems: isExpanded ? "flex-start" : "center",
            flexDirection: headerDirection as any,
          },
        ]}
      >
        {isExpanded ? (
          <ChevronUpIcon size={24} color={colors.fontPrimary} />
        ) : (
          <ChevronDownIcon size={24} color={colors.fontPrimary} />
        )}

        <View style={styles.faqContent}>
          <View
            style={[
              styles.questionRow,
              { flexDirection: contentDirection as any },
            ]}
          >
            {isRTL ? (
              <>
                <Typography
                  variant="body"
                  color="primary"
                  style={[styles.questionText, Platform.OS === 'web' && isRTL && { textAlign: 'right' }]}
                >
                  {item.question}
                </Typography>
                <Typography variant="headline" color="primary">
                  {questionLabel}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="headline" color="primary">
                  {questionLabel}
                </Typography>
                <Typography
                  variant="body"
                  color="primary"
                  style={[styles.questionText, Platform.OS === 'web' && isRTL && { textAlign: 'right' }]}
                >
                  {item.question}
                </Typography>
              </>
            )}
          </View>
          {isExpanded && (
            <View
              style={[
                styles.answerRow,
                { flexDirection: contentDirection as any },
              ]}
            >
              {isRTL ? (
                <>
                  <Typography
                    variant="body"
                    color="tertiary"
                    style={[styles.answerText, Platform.OS === 'web' && isRTL && { textAlign: 'right' }]}
                  >
                    {item.answer}
                  </Typography>
                  <Typography variant="headline" color="primary">
                    {answerLabel}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="headline" color="primary">
                    {answerLabel}
                  </Typography>
                  <Typography
                    variant="body"
                    color="tertiary"
                    style={[styles.answerText, Platform.OS === 'web' && isRTL && { textAlign: 'right' }]}
                  >
                    {item.answer}
                  </Typography>
                </>
              )}
            </View>
          )}
        </View>
      </Pressable>
    </View>
  );
}

export function FaqList({ items }: FaqListProps) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const questionLabel = t("profile.faqQuestionLabel");
  const answerLabel = t("profile.faqAnswerLabel");

  const toggle = (idx: number) => {
    const next = new Set(expanded);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    setExpanded(next);
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <View style={styles.faqContainer}>
      {items.map((item, index) => {
        return (
          <FaqListItem
            key={`${item.question}-${index}`}
            item={item}
            index={index}
            isExpanded={expanded.has(index)}
            onToggle={toggle}
            questionLabel={questionLabel}
            answerLabel={answerLabel}
            colors={colors}
          />
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  faqContainer: {
    gap: 12,
  },
  faqItem: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
  },
  faqHeader: {
    flexDirection: "row",
    gap: 16,
  },
  faqContent: {
    flex: 1,
    gap: 16,
  },
  questionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  answerRow: {
    flexDirection: "row",
    gap: 8,
  },
questionText: {
  flex: 1,
  ...(Platform.OS === 'web' ? {} : {
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 22,
  }),
},
  answerText: {
    flex: 1,
  },
});
